import { useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AlertTriangle, CheckCircle, UserPlus, X } from 'lucide-react';
import { useAdminRegistrationStore } from '../../stores/adminRegistrationStore';
import { useInclusionRequestStore } from '../../stores/inclusionRequestStore';
import { useDevis } from '../../lib/orders/useDevis';
import { produireEtEnvoyerFacture } from '../../lib/orders/gateway.supabase';
import { resultatInscriptionAdmin, type ResultatInscription } from '../../lib/orders/resultatInscriptionAdmin';
import { remainingPlaces } from '../../lib/places';
import { demandesDe, inscriptionsOuvertesPour, semainesAInscrire, type GroupeATraiter } from '../../lib/inclusion/regroupement';
import { DESCRIPTION_DES_NIVEAUX } from '../../lib/inclusion/niveaux';
import RecapitulatifInscription from './inscription/RecapitulatifInscription';
import Telephone from './Telephone';

interface InscriptionDepuisDemandeModalProps {
  groupe: GroupeATraiter;
  onClose: () => void;
  /** Après une inscription : la liste se relit. */
  onInscrit: () => void;
}

const date = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR');
const dateEtHeure = (iso: string) =>
  new Date(iso).toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' });

/**
 * Accepter une demande d'inclusion, c'est inscrire l'enfant (décision
 * d'Éclosion du 26/10/2026) : l'administration confirme l'inscription aux
 * semaines retenues — jamais avant l'ouverture de leurs inscriptions —, la
 * facture part aux parents, l'enfant est réellement inscrit. Elle place aussi
 * l'enfant au niveau 1 (ses parents l'inscriront comme tout enfant) ou 2
 * (chaque inscription par une demande), et le parent reçoit une réponse qui
 * le dit.
 *
 * C'est le chemin de « Inscription admin » et de la liste d'attente, et lui
 * seul : le même devis (`quote_order`), la même commande (`create_order`, qui
 * crée l'inscription et la facture), le même envoi de la facture. Les demandes
 * de ces semaines passent ensuite en `converted`, doublons compris.
 */
const InscriptionDepuisDemandeModal = ({ groupe, onClose, onInscrit }: InscriptionDepuisDemandeModalProps) => {
  const { createAdminRegistration, isSubmitting } = useAdminRegistrationStore();
  const { deciderLesDemandes, poserNiveau, prevenirLeParent } = useInclusionRequestStore();

  const possibles = semainesAInscrire(groupe);
  const [choisies, setChoisies] = useState<string[]>(possibles.map((s) => s.sessionId));
  /* Après examen, l'enfant est au niveau 1 ou 2 : un enfant pas encore examiné n'a pas de choix d'office. */
  const [niveau, setNiveau] = useState<1 | 2 | null>(groupe.niveau === 1 || groupe.niveau === 2 ? groupe.niveau : null);
  const [mot, setMot] = useState('');
  const [reduit, setReduit] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [resultat, setResultat] = useState<ResultatInscription | null>(null);
  const [enCours, setEnCours] = useState(false);

  const semaines = possibles.filter((s) => choisies.includes(s.sessionId));
  const cleChoix = choisies.join(',');
  const lignes = useMemo(
    () => cleChoix.split(',').filter(Boolean).map((sessionId) => ({ session_id: sessionId, kid_id: groupe.kidId })),
    [cleChoix, groupe.kidId],
  );
  const { devis, chargement: devisEnCours, erreur: erreurDevis } = useDevis(groupe.parentId, lignes, reduit, codes);
  const occupe = isSubmitting || enCours;
  const prenom = groupe.enfant.prenom;

  const basculer = (sessionId: string) =>
    setChoisies((c) => (c.includes(sessionId) ? c.filter((x) => x !== sessionId) : [...c, sessionId]));

  const confirmer = async () => {
    setErreur(null);
    if (semaines.length === 0) {
      setErreur('Cochez au moins une semaine.');
      return;
    }
    if (niveau === null) {
      setErreur(`Choisissez le niveau d'accès de ${prenom} pour ses prochaines inscriptions.`);
      return;
    }
    setEnCours(true);
    let inscrite: Awaited<ReturnType<typeof createAdminRegistration>>;
    try {
      inscrite = await createAdminRegistration(groupe.parentId, semaines.map((s) => ({
        id: `${s.sessionId}-${groupe.kidId}`,
        activity_id: s.sessionId,
        kid_id: groupe.kidId,
        kidName: `${groupe.enfant.prenom} ${groupe.enfant.nom}`,
        activityName: s.stage,
        activityCategory: s.centre,
        dateRange: `${date(s.debut)} - ${date(s.fin)}`,
      })), reduit, codes);
    } catch (err) {
      // Rien n'est écrit : la fenêtre reste ouverte, on peut corriger et recommencer.
      setErreur(err instanceof Error && err.message ? err.message : "L'inscription n'a pas pu être créée.");
      setEnCours(false);
      return;
    }

    /*
      L'enfant est inscrit et facturé. La suite ne peut plus défaire cela : un
      geste qui échoue se dit, sans rien annuler, et une seconde tentative de
      commande serait refusée par `create_order` (« déjà inscrit »).
    */
    const avertissements: string[] = [];
    let acceptees: string[] = [];
    try {
      acceptees = await deciderLesDemandes(demandesDe(semaines), 'converted', { mot_au_parent: mot });
    } catch {
      avertissements.push(
        'Les demandes n\'ont pas pu être marquées « acceptées », et la réponse au parent n\'est pas partie : '
        + 'classez-les depuis « À traiter » et prévenez le parent vous-même.',
      );
    }
    /* Le niveau avant le courriel : celui-ci dit au parent comment se feront les prochaines inscriptions. */
    if (niveau !== groupe.niveau) {
      try {
        await poserNiveau(groupe.kidId, niveau, 'Demande d\'inclusion acceptée');
      } catch {
        avertissements.push(`Le niveau ${niveau} n'a pas pu être enregistré : posez-le depuis la fiche de l'enfant.`);
      }
    }
    const envoi = await produireEtEnvoyerFacture(inscrite.invoiceNumber, groupe.parent.email).catch((e: unknown) => ({
      pdfUrl: null,
      erreur: e instanceof Error ? e.message : 'L\'envoi de la facture a échoué.',
    }));
    let parentPrevenu = false;
    if (acceptees.length > 0) {
      try {
        const r = await prevenirLeParent(acceptees);
        parentPrevenu = r.envoye || r.deja === true;
      } catch {
        parentPrevenu = false;
      }
      if (!parentPrevenu) {
        avertissements.push('La réponse au parent n\'est pas partie : « Prévenir le parent » dans les demandes acceptées.');
      }
    }

    const r = resultatInscriptionAdmin(inscrite.invoiceNumber, inscrite.commande, envoi, groupe.parent.email ?? '');
    setResultat({
      type: r.type === 'success' && avertissements.length === 0 ? 'success' : 'error',
      message: [
        r.message,
        parentPrevenu
          ? `Réponse envoyée au parent : ${niveau === 1
            ? `les prochaines inscriptions de ${prenom} se feront sans demande (niveau 1).`
            : `les prochaines inscriptions de ${prenom} passeront encore par une demande (niveau 2).`}`
          : null,
        ...avertissements.map((a) => `⚠ ${a}`),
      ].filter(Boolean).join('\n\n'),
    });
    setEnCours(false);
    onInscrit();
  };

  const fermer = () => {
    if (!occupe) onClose();
  };

  return (
    <Dialog open onClose={fermer} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />

        <div className="relative w-full max-w-3xl rounded-lg bg-white p-4 shadow-xl sm:p-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="flex items-center text-lg font-semibold">
              <UserPlus className="mr-2 h-5 w-5 text-teal-600" />
              Accepter et inscrire {prenom}
            </Dialog.Title>
            <button onClick={fermer} className="text-gray-400 hover:text-gray-600" aria-label="Fermer">
              <X className="h-5 w-5" />
            </button>
          </div>

          <dl className="mb-6 grid gap-4 rounded-lg bg-gray-50 p-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase text-gray-500">Parent</dt>
              <dd className="mt-1 font-medium text-gray-900">{groupe.parent.prenom} {groupe.parent.nom}</dd>
              <dd className="break-all text-gray-600">{groupe.parent.email || 'Aucune adresse'}</dd>
              <dd className="text-gray-600"><Telephone numero={groupe.parent.telephone} /></dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase text-gray-500">Enfant</dt>
              <dd className="mt-1 font-medium text-gray-900">{groupe.enfant.prenom} {groupe.enfant.nom}</dd>
              <dd className="text-gray-600">En inclusion : reste compté·e ainsi (accueil, rapport ONE).</dd>
            </div>
          </dl>

          {resultat ? (
            <div className="flex flex-col items-center text-center">
              {resultat.type === 'success' ? (
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-7 w-7 text-green-600" />
                </div>
              ) : (
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
                  <AlertTriangle className="h-7 w-7 text-amber-600" />
                </div>
              )}
              <p className="mb-6 whitespace-pre-line text-sm text-gray-600">{resultat.message}</p>
              <button onClick={onClose} className="btn-primary px-6">Fermer</button>
            </div>
          ) : (
            <>
              <fieldset className="mb-6">
                <legend className="mb-2 text-sm font-medium text-gray-900">Les semaines à inscrire</legend>
                <ul className="divide-y rounded-lg border">
                  {groupe.semaines.map((s) => {
                    const libres = s.capacite === null ? null : remainingPlaces({ capacity: s.capacite, current_registrations: s.inscrits });
                    const ouverte = inscriptionsOuvertesPour(s);
                    return (
                      <li key={s.sessionId} className="flex items-start gap-3 p-3 text-sm">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={!s.dejaInscrit && ouverte && choisies.includes(s.sessionId)}
                          disabled={s.dejaInscrit || !ouverte || occupe}
                          onChange={() => basculer(s.sessionId)}
                          aria-label={`Inscrire du ${date(s.debut)} au ${date(s.fin)}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-gray-900">
                            Du {date(s.debut)} au {date(s.fin)} — {s.stage}
                          </span>
                          <span className="block text-gray-600">
                            {s.centre}{s.periode ? ` · ${s.periode}` : ''}
                            {libres !== null && ` · ${libres > 0 ? `${libres} place${libres > 1 ? 's' : ''} libre${libres > 1 ? 's' : ''}` : 'complet'}`}
                          </span>
                          {s.dejaInscrit && <span className="block text-gray-500">Déjà inscrit·e : rien à refaire.</span>}
                          {!s.dejaInscrit && !ouverte && s.ouverture && (
                            <span className="block text-amber-700">
                              Inscriptions ouvertes le {dateEtHeure(s.ouverture)} : pas d'inscription avant.
                            </span>
                          )}
                          {!s.dejaInscrit && libres === 0 && (
                            <span className="block text-amber-700">Complet : l'inscription sera refusée tant qu'aucune place n'est libre.</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>

              <fieldset className="mb-6 space-y-2 text-sm">
                <legend className="mb-2 font-medium text-gray-900">Le niveau d'accès de {prenom}, pour ses prochaines inscriptions</legend>
                {groupe.niveau === null && (
                  <p className="text-xs text-amber-800">Pas encore examiné : choisissez son niveau.</p>
                )}
                {([1, 2] as const).map((n) => (
                  <label key={n} className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="niveau-acces"
                      className="mt-1"
                      checked={niveau === n}
                      onChange={() => {
                        setNiveau(n);
                        setErreur(null);
                      }}
                      disabled={occupe}
                    />
                    <span>
                      <span className="font-medium">Niveau {n} — {DESCRIPTION_DES_NIVEAUX[n].titre}</span>
                      <span className="block text-gray-500">{DESCRIPTION_DES_NIVEAUX[n].description}</span>
                    </span>
                  </label>
                ))}
                <p className="text-xs text-gray-500">{prenom} reste en inclusion pour l'accueil et le rapport ONE, quel que soit le niveau.</p>
              </fieldset>

              <label className="mb-6 block text-sm">
                <span className="mb-1 block font-medium text-gray-900">Un mot pour le parent (facultatif)</span>
                <textarea
                  value={mot}
                  onChange={(e) => setMot(e.target.value)}
                  disabled={occupe}
                  rows={2}
                  className="w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Repris dans le courriel de confirmation."
                />
              </label>

              {erreur && (
                <div className="mb-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                  <p className="text-sm text-red-700">{erreur}</p>
                </div>
              )}

              <RecapitulatifInscription
                kidId={groupe.kidId}
                semaines={semaines.map((s) => ({
                  sessionId: s.sessionId, stage: s.stage, centre: s.centre, dates: `${date(s.debut)} - ${date(s.fin)}`,
                }))}
                devis={devis}
                devisEnCours={devisEnCours}
                erreurDevis={erreurDevis}
                reduit={reduit}
                onBasculerReduit={() => setReduit(!reduit)}
                codes={codes}
                onCodes={setCodes}
                enCours={occupe}
                onConfirmer={confirmer}
                onRetour={fermer}
                libelleRetour="Annuler"
              />
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default InscriptionDepuisDemandeModal;
